<div class="course-summary-view h-full flex flex-col" role="region" aria-label="Course summary" data-testid="course-summary-section">
  <!-- Header Bar -->
  <app-course-player-header
    [title]="'Course Summary'"
    [currentIndex]="currentItemIndex"
    [totalItems]="totalItems"
    [canGoToPrevious]="canGoToPrevious"
    [canGoToNext]="canGoToNext"
    [showNavigation]="true"
    [showTimelineToggle]="true"
    [timelineVisible]="showTimeline"
    [showFullscreen]="showFullscreen"
    [isFullscreen]="isFullscreen"
    [showCloseButton]="true"
    [mode]="playerMode"
    (onNavigatePrevious)="onNavigatePrevious.emit()"
    (onNavigateNext)="onNavigateNext.emit()"
    (onTimelineToggle)="onTimelineToggle.emit()"
    (onFullscreenToggle)="onFullscreenToggle.emit()"
    (onClose)="onCloseModal.emit()">
  </app-course-player-header>

  <!-- Content -->
  <!-- KAN-577 B: min-h-0 is load-bearing. A flex item defaults to
       min-height:auto and refuses to shrink below its content height, so
       without it this div grows past the panel, overflow-y:auto finds nothing
       to overflow, no scrollbar appears, and the certificate card at the bottom
       is unreachable. -->
  <!-- KAN-702: p-4 on phones. Two nested 32px paddings (this div and each
       card) left a ~100px title column at 360px; from sm the 32px returns. -->
  <div class="flex-1 min-h-0 overflow-y-auto p-4 sm:p-8 bg-base-200" data-testid="course-summary-scroll">
    <div class="max-w-4xl mx-auto">
      <!-- Completion Header -->
      <div class="bg-base-100 rounded-lg shadow-lg p-4 sm:p-8 mb-6" data-testid="course-summary-hero-card">
        <!-- KAN-702: stacked on phones (thumbnail above the title), side by side
             from sm. Beside a 128px thumbnail the title column was ~100px wide
             and overflow-wrap:anywhere broke words mid-word. No items-center
             below sm: stacked, the title column must stretch full width. -->
        <div class="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-testid="course-summary-hero">
          <!-- Course Thumbnail -->
          @if (courseData?.thumbnail) {
            <div class="flex-shrink-0">
              <img [src]="courseData.thumbnail"
                [alt]="courseData?.title || courseData?.name"
                class="rounded-full shadow-lg w-20 h-20 sm:w-32 sm:h-32 object-cover"
                data-testid="course-summary-thumbnail">
            </div>
          }

          <!-- Course Info -->
          <div class="flex-1 min-w-0">
            <h2 class="course-summary-title text-2xl sm:text-3xl font-bold mb-2"
                [title]="courseData?.title || courseData?.name || courseStructure?.courseName"
                data-testid="course-summary-title-heading">{{ courseData?.title || courseData?.name || courseStructure?.courseName }}</h2>
            <p class="text-lg text-base-content/70 mb-4">
              You've completed all modules in this course
            </p>

            <!-- Course Stats -->
            <div class="flex items-center gap-3">
              <!-- KAN-702: h-auto lets the pill grow if its label wraps; the fixed
                   .badge-lg height let wrapped text spill above and below it. -->
              <div class="badge badge-lg badge-success gap-2 h-auto max-w-full" data-testid="course-summary-completion-badge">
                <app-icon name="check_circle" size="sm"></app-icon>
                {{ getCourseCompletionStats().completedModules }}/{{ getCourseCompletionStats().totalModules }} Modules Complete
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Module List -->
      <div class="bg-base-100 rounded-lg shadow-lg p-4 sm:p-8 mb-6" data-testid="course-summary-progress-card">
        <h3 class="text-2xl font-bold mb-4" data-testid="course-summary-progress-heading">Course Progress</h3>

        <!-- KAN-465: running cumulative points score across attempted quizzes,
             against the course passing threshold. Same pure util as the
             timeline sidebar so the two surfaces never disagree. -->
        @let runningScore = getRunningScore();
        @if (runningScore.percent !== null) {
          <div class="mb-4 rounded-lg bg-base-200 p-4" data-testid="course-summary-running-score">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <span class="text-sm text-base-content/70">Running score:</span>
              <span class="font-semibold" data-testid="course-summary-running-score-points">{{ runningScore.pointsText }} ({{ runningScore.percentText }})</span>
            </div>
            @if (runningScore.threshold !== null) {
              <div class="mt-1 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span class="text-base-content/60" data-testid="course-summary-running-score-threshold">Course passing: {{ runningScore.threshold }}%</span>
                @if (runningScore.belowThreshold) {
                  <span class="font-medium text-error" data-testid="course-summary-running-score-state">Below passing</span>
                } @else {
                  <span class="font-medium text-success" data-testid="course-summary-running-score-state">On track</span>
                }
              </div>
            }
          </div>
        }
        <div class="space-y-2" data-testid="course-summary-progress-list">
          @for (group of getGroupedProgressItems(); track group) {
            <!-- Unit Group -->
            @if (group.type === 'unit') {
              <!-- Unit Header -->
              <div class="flex items-center gap-3 p-3 rounded bg-base-300/50 mt-2">
                <app-icon name="folder" size="sm" class="text-base-content/60 flex-shrink-0"></app-icon>
                <div class="flex-1 min-w-0">
                  <div class="course-summary-row-name font-semibold" [title]="group.unitName + ' - Overview'">{{ group.unitName }} - Overview</div>
                </div>
              </div>
              <!-- Unit Modules (indented) -->
              <!-- KAN-702: on phones each row wraps and its text column takes
                   the whole first line, so the status badge drops to its own
                   line (right-anchored) instead of covering the nowrap score
                   pill. From sm the column shrinks (min-w-0) and the badge sits
                   on the name's line as before (KAN-211). -->
              @for (item of group.items; track item) {
                <div
                  class="flex flex-wrap items-center gap-3 p-3 rounded bg-base-200 ml-3 sm:ml-6 border-l-2 border-base-300"
                  data-testid="course-summary-module-row">
                  <app-icon [name]="getModuleIcon(item)" size="sm" [class]="getModuleIconClass(item)"></app-icon>
                  <div class="flex-1 min-w-0 max-sm:min-w-[calc(100%-3rem)]">
                    <div class="course-summary-row-name font-medium" [title]="item.name">{{ item.name }}</div>
                    @let unitScoreParts = getScorePartsForModule(item.id, item.passingScore ?? 80);
                    @if (isPriorCreditForModule(item.id)) {
                      <div class="text-xs" data-testid="course-summary-prior-credit">
                        <span class="badge badge-success badge-xs whitespace-nowrap">Credited</span> for prior completion
                        @if (getInheritedSourceCourseTitle(item.id); as src) {
                          <small class="opacity-60" data-testid="course-summary-inherited-source">from {{ src }}</small>
                        }
                      </div>
                    } @else if (unitScoreParts.attempted) {
                      @let unitItemPassed = isQuizPassedForItem(item);
                      @let unitFloor = getFloorViolationForItem(item);
                      @let unitGate = getGateViolationForItem(item);
                      <div class="text-xs">
                        Score: <span class="badge badge-secondary badge-xs whitespace-nowrap">
                          @if (unitScoreParts.showPoints) {
                            {{ unitScoreParts.pointsText }}
                            (<span data-testid="course-summary-quiz-percentage">{{ unitScoreParts.percentText }}</span>)
                          } @else {
                            <span data-testid="course-summary-quiz-percentage">{{ unitScoreParts.percentText }}</span>
                          }
                        </span>
                        <span [class.text-success]="unitItemPassed"
                          [class.text-error]="!unitItemPassed">
                          ({{ unitItemPassed ? 'Passed' : 'Failed' }})
                        </span>
                        <!-- KAN-410: surface the unit-level points-floor / per-quiz
                             gate violation exactly like the Unit Overview so the two
                             views never contradict. -->
                        @if (unitFloor) {
                          <div class="text-error mt-1" data-testid="course-summary-floor-violation">
                            {{ unitFloor.quizTitle || item.name }}: {{ unitFloor.earnedPoints }} pts - minimum {{ unitFloor.minRequired }} not met
                          </div>
                        }
                        @if (unitGate) {
                          <div class="text-error mt-1" data-testid="course-summary-gate-violation">
                            {{ unitGate.quizTitle || item.name }}: did not meet its own passing score
                          </div>
                        }
                      </div>
                    }
                </div>
                <div class="badge flex-shrink-0 ml-auto" [class.badge-success]="isModuleComplete(item.id)" data-testid="course-summary-module-status">
                  {{ isModuleComplete(item.id) ? 'Complete' : 'In Progress' }}
                </div>
              </div>
            }
          }
          <!-- Standalone Module (not in a unit) -->
          @if (group.type === 'direct') {
            @for (item of group.items; track item) {
              <div
                class="flex flex-wrap items-center gap-3 p-3 rounded bg-base-200"
                data-testid="course-summary-module-row">
                <app-icon [name]="getModuleIcon(item)" size="sm" [class]="getModuleIconClass(item)"></app-icon>
                <div class="flex-1 min-w-0 max-sm:min-w-[calc(100%-3rem)]">
                  <div class="course-summary-row-name font-medium" [title]="item.name">{{ item.name }}</div>
                  @let directScoreParts = getScorePartsForModule(item.id, item.passingScore ?? 80);
                  @if (isPriorCreditForModule(item.id)) {
                    <div class="text-xs" data-testid="course-summary-prior-credit">
                      <span class="badge badge-success badge-xs whitespace-nowrap">Credited</span> for prior completion
                      @if (getInheritedSourceCourseTitle(item.id); as src) {
                        <small class="opacity-60" data-testid="course-summary-inherited-source">from {{ src }}</small>
                      }
                    </div>
                  } @else if (directScoreParts.attempted) {
                    @let directItemPassed = isQuizPassedForItem(item);
                    <div class="text-xs">
                      Score: <span class="badge badge-secondary badge-xs whitespace-nowrap">
                        @if (directScoreParts.showPoints) {
                          {{ directScoreParts.pointsText }}
                          (<span data-testid="course-summary-quiz-percentage">{{ directScoreParts.percentText }}</span>)
                        } @else {
                          <span data-testid="course-summary-quiz-percentage">{{ directScoreParts.percentText }}</span>
                        }
                      </span>
                      <span [class.text-success]="directItemPassed"
                        [class.text-error]="!directItemPassed">
                        ({{ directItemPassed ? 'Passed' : 'Failed' }})
                      </span>
                    </div>
                  }
              </div>
              <div class="badge flex-shrink-0 ml-auto" [class.badge-success]="isModuleComplete(item.id)" data-testid="course-summary-module-status">
                {{ isModuleComplete(item.id) ? 'Complete' : 'In Progress' }}
              </div>
            </div>
          }
        }
        <!-- Course Summary. KAN-411 round 3: only claim "Course Complete /
             Completed" when the course GENUINELY completed (modules done AND the
             pass gate satisfied). A gate-blocked course shows a neutral state -
             the modal's "Course not passed yet" banner explains why.
             Round 4: that banner now renders the OVERALL verdict and names the
             blocking rule, so this claim finally holds for a floor/gate block
             too - it used to say "Passed" in green right above this row. -->
        @if (group.type === 'summary') {
          @for (item of group.items; track item) {
            @if (courseComplete) {
              <div
                class="flex items-center gap-3 p-3 rounded bg-success/10 border border-success/30 mt-2">
                <app-icon name="check_circle" size="sm" class="text-success flex-shrink-0"></app-icon>
                <div class="flex-1 min-w-0">
                  <div class="course-summary-row-name font-semibold" [title]="item.name">{{ item.name }}</div>
                  <div class="text-xs text-success/80">Course Complete</div>
                </div>
                <div class="badge badge-success flex-shrink-0" data-testid="course-summary-completion-verdict">Completed</div>
              </div>
            } @else {
              <div
                class="flex items-center gap-3 p-3 rounded bg-base-300/40 border border-base-300 mt-2">
                <app-icon name="info" size="sm" class="text-base-content/60 flex-shrink-0"></app-icon>
                <div class="flex-1 min-w-0">
                  <div class="course-summary-row-name font-semibold" [title]="item.name">{{ item.name }}</div>
                  <div class="text-xs text-base-content/60">Not yet passed</div>
                </div>
                <div class="badge badge-ghost flex-shrink-0" data-testid="course-summary-completion-verdict">In Progress</div>
              </div>
            }
          }
        }
      }
    </div>
  </div>

  <!-- Certificate (if available) -->
  <!-- KAN-411 round 3: only offer the certificate when the course GENUINELY
       completed - a gate-blocked course had no certificate issued.
       KAN-406: read the FLAT certificateEnabled the GET /courses/{id} response
       carries (serializer camelizes certificate_enabled); the nested
       certificate.enabled write-shape is not on the read response. -->
  @if ((courseData?.certificateEnabled ?? courseData?.certificate?.enabled) && courseComplete) {
    <div class="bg-base-100 rounded-lg shadow-lg p-4 sm:p-8 mb-6" data-testid="course-summary-cert-card">
      <h3 class="text-2xl font-bold mb-4">Certificate of Completion</h3>
      <p class="text-base-content/70 mb-4">
        Congratulations! You've earned a certificate for completing this course.
      </p>
      <!-- KAN-679: this button had no click binding. It now opens the learner's
           certificate in the viewer (the same page My Courses > My Certificates
           links to); "View", not "Download", because that page prints. -->
      <button
        class="btn btn-primary"
        aria-label="View certificate"
        data-testid="course-summary-download-cert-btn"
        [disabled]="isPreviewMode || certificateLookup() === 'loading'"
        [attr.aria-busy]="certificateLookup() === 'loading'"
        (click)="viewCertificate()">
        @if (certificateLookup() === 'loading') {
          <span class="loading loading-spinner loading-sm"></span>
        } @else {
          <app-icon name="open_in_new" size="sm"></app-icon>
        }
        View Certificate
      </button>
      @if (isPreviewMode) {
        <p class="text-sm text-base-content/60 mt-3" data-testid="course-summary-cert-preview-note">
          Preview: learners open their certificate from here.
        </p>
      } @else if (certificateLookup() === 'not-ready') {
        <p class="text-sm text-base-content/70 mt-3" role="status" data-testid="course-summary-cert-not-ready">
          Your certificate isn't ready yet. You'll find it under My Courses &gt; My Certificates.
        </p>
      }
    </div>
  }

  <!-- Actions -->
  <!-- KAN-691: stacked on phones, where side by side each button was ~150px
       wide and "Review Last Module" wrapped onto three lines inside the fixed
       3rem .btn height, spilling past its border. h-auto + py-3 let a button
       grow if its label ever wraps; one line still renders at 3rem. -->
  <div class="flex flex-col sm:flex-row gap-3" data-testid="course-summary-actions">
    <button class="btn btn-outline flex-1 h-auto py-3" (click)="onNavigatePrevious.emit()" data-testid="course-summary-review-btn">
      <app-icon name="arrow_back" size="sm"></app-icon>
      Review Last Module
    </button>
    <button class="btn btn-primary flex-1 h-auto py-3" (click)="onCloseModal.emit()" data-testid="course-summary-exit-btn">
      <app-icon name="check" size="sm"></app-icon>
      Exit Course
    </button>
  </div>
</div>
</div>
</div>
